import { useCallback, useEffect, useState } from "react";
import type { Ad } from "@/lib/data";

const KEY = "albazar:favorites";

type Listener = () => void;
const listeners = new Set<Listener>();

function read(): Ad[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as Ad[]) : [];
  } catch {
    return [];
  }
}

function write(list: Ad[]) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    /* kvota dolu ola bilər */
  }
  listeners.forEach((l) => l());
}

export function getFavorites(): Ad[] {
  return read();
}

export function toggleFavorite(ad: Ad): boolean {
  const list = read();
  const exists = list.some((a) => a.id === ad.id);
  write(exists ? list.filter((a) => a.id !== ad.id) : [ad, ...list]);
  return !exists;
}

function subscribe(cb: Listener) {
  listeners.add(cb);
  window.addEventListener("storage", cb);
  return () => {
    listeners.delete(cb);
    window.removeEventListener("storage", cb);
  };
}

export function useFavorites() {
  const [items, setItems] = useState<Ad[]>([]);

  useEffect(() => {
    const sync = () => setItems(read());
    sync();
    return subscribe(sync);
  }, []);

  const toggle = useCallback((ad: Ad) => toggleFavorite(ad), []);
  const isFavorite = useCallback((id: string) => items.some((a) => a.id === id), [items]);

  return { items, toggle, isFavorite };
}
